import { CITY } from '@city'
import { useEffect, useState } from 'react'
import type { ReactElement } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { GlobalHeader } from '../components/stationPage/GlobalHeader'
import { StationNav } from '../components/stationPage/StationNav'
import { StationOverview } from '../components/stationPage/StationOverview'
import { StationLinesSection } from '../components/stationPage/StationLinesSection'
import { StationArrivalsSection } from '../components/stationPage/StationArrivalsSection'
import { StationTimetableSection } from '../components/stationPage/StationTimetableSection'
import { StationExitsSection } from '../components/stationPage/StationExitsSection'
import { StationFacilitiesSection } from '../components/stationPage/StationFacilitiesSection'
import { StationAboutSection } from '../components/stationPage/StationAboutSection'
import { streetFromAddress, stationLandmarks } from '../components/stationPage/aboutStationCopy'
import { StationFaqSection } from '../components/stationPage/StationFaqSection'
import { StationAppPromo } from '../components/stationPage/StationAppPromo'
import { SiteFooter } from '../components/stationPage/SiteFooter'
import { buildStationFaq, exitLandmarkFromExits } from '../components/stationPage/stationFaqEntries'
import { accessibilityFacts, facilityAvailability } from '../components/stationPage/facilityCatalog'
import { openTodayRange } from '../components/stationPage/openTodayRange'
import {
  buildStationPageData,
  readEmbeddedStationPageData,
} from '../components/stationPage/stationPageData'
import {
  fetchStationContent,
  type StationContent,
} from '../components/stationPage/fetchStationContent'
import { lineHexForTheme } from '../schema/lineColor'
import { useStationPageTheme } from '../components/stationPage/useStationPageTheme'
import { StationStatusController } from '../components/StationStatusController'
import { StationPageMeta } from '../components/stationPage/StationPageMeta'
import { fetchSchema } from '../schema/fetchSchema'
import type { SchemaLine, SchemaStation, SchemaStationTimetable } from '../schema/types'
import styles from './StationPage.module.css'

type LoadState =
  | { phase: 'loading' }
  | {
      phase: 'ready'
      station: SchemaStation
      timetable: SchemaStationTimetable | undefined
      otherLines: readonly SchemaLine[]
      // The station's default record shown while a `?line-id=` record
      // the prerender did not bake is still loading.
      provisional?: true
    }
  | { phase: 'not-found' }
  // Schema fetch failed — a network condition, NOT an unknown station.
  // Rendering the not-found copy here is what taught Google to Soft-404
  // the prerendered detail pages (GSC, 2026-08-17).
  | { phase: 'error' }

/**
 * Assembled station-page template (TASK-699, design TASK-584, Figma
 * 8597:41520), mounted at `/:locale/station/:stationId` — accepts a
 * NAPTAN id, SEO slug or schema id (see `resolveStationRef`).
 * `?line-id=` picks that line's record out of a multi-line cluster
 * (and excludes it from the "Other lines" block).
 *
 * Deliberately a separate route from the canonical
 * `/:locale/stations/:slug` surface: that URL is Google-indexed and
 * still drives the map's station panel. The cutover (which URL shows
 * which surface) is a product+SEO decision taken when the template's
 * sections are complete — not a side effect of scaffolding.
 *
 * Every `STATION_NAV_SECTIONS` id has a live section on the page
 * (the last dev stub left with the Nearby tab, 2026-08-04; both
 * return together when that design lands).
 *
 * The `station-page` class on `<html>` unlocks document scrolling for
 * the page's lifetime — same escape hatch as `legal-page` (the map SPA
 * locks the document for the WebGL canvas; see index.css).
 */
export function StationPage(): ReactElement {
  const { locale = 'en', stationId = '' } = useParams<{ locale: string; stationId: string }>()
  const [params] = useSearchParams()
  const { t } = useTranslation('station-page')
  // Load-error copy — already localized for every catalog locale by the
  // StationDetails panel; reused verbatim so the error phase ships with
  // zero new translation keys.
  const { t: tError } = useTranslation('station-details')
  // Picks this line's record out of a multi-line cluster (roundel line,
  // "Other lines" exclusion). Absent/unknown → default cluster pick.
  const lineId = params.get('line-id') ?? undefined

  // Prerendered pages embed the ready-state payload (see
  // stationPageData.ts) — reading it SYNCHRONOUSLY (initializer +
  // render adjustment below) makes the very first render `ready`, so
  // mounting the SPA never blanks the page while the 28.9 MB
  // schema.json downloads (the Soft-404 mechanism, GSC 2026-08-17).
  const routeKey = `${stationId}|${lineId ?? ''}`
  const readState = (): LoadState => {
    const embedded = readEmbeddedStationPageData(stationId, lineId)
    if (embedded !== null) return { phase: 'ready', ...embedded }
    // `?line-id=` (the "Other lines" rows) asks for a record the
    // prerender did not bake. The same station's default record stays
    // on screen until the schema arrives: an empty page meanwhile is
    // what GSC filed under Soft 404 (2026-10-20 crawl).
    const fallback = lineId === undefined ? null : readEmbeddedStationPageData(stationId, undefined)
    return fallback !== null
      ? { phase: 'ready', ...fallback, provisional: true }
      : { phase: 'loading' }
  }
  const [state, setState] = useState<LoadState>(readState)
  const [prevRouteKey, setPrevRouteKey] = useState(routeKey)
  // Render adjustment (not an effect): SPA navigation to another
  // station re-reads the embed or drops back to `loading`, which the
  // fetch effect below picks up.
  if (prevRouteKey !== routeKey) {
    setPrevRouteKey(routeKey)
    setState(readState())
  }
  // Editorial record (address, description, exits) — one fetch for
  // every section that reads the station-content database.
  const [content, setContent] = useState<StationContent>({})

  useEffect(() => {
    const html = document.documentElement
    html.classList.add('station-page')
    return () => {
      html.classList.remove('station-page')
    }
  }, [])

  // Fetches only while `loading` or `provisional` — pages served from
  // the embedded payload never download the schema. The error state's
  // Retry button re-enters `loading` from its click handler, re-arming
  // this effect. A provisional page that cannot refine keeps the
  // station it already shows: a failed refinement is not a dead page.
  const needsSchema =
    state.phase === 'loading' || (state.phase === 'ready' && state.provisional === true)
  useEffect(() => {
    if (!needsSchema) return
    let cancelled = false
    const keepShown = (fallback: LoadState) =>
      setState((current) =>
        current.phase === 'ready' ? { ...current, provisional: undefined } : fallback,
      )
    fetchSchema()
      .then((schema) => {
        if (cancelled) return
        const data = buildStationPageData(schema, stationId, lineId)
        if (data === null) keepShown({ phase: 'not-found' })
        else setState({ phase: 'ready', ...data })
      })
      .catch(() => {
        if (!cancelled) keepShown({ phase: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [needsSchema, stationId, lineId])

  // Line hexes must repaint with the CSS tokens (pill == map contract).
  const theme = useStationPageTheme()

  const naptanId = state.phase === 'ready' ? (state.station.naptanId ?? '') : ''
  useEffect(() => {
    if (naptanId === '') return
    let cancelled = false
    void fetchStationContent(naptanId, locale).then((c) => {
      if (!cancelled) setContent(c)
    })
    return () => {
      cancelled = true
    }
  }, [naptanId, locale])

  if (state.phase === 'loading') {
    return <div className={styles.page} />
  }

  if (state.phase === 'error') {
    return (
      <div className={styles.page}>
        <GlobalHeader locale={locale} stationName={tError('station_details_error_title')} />
        <main className={styles.notFound}>
          <h1>{tError('station_details_error_title')}</h1>
          <p>{tError('station_details_error_subtitle')}</p>
          <button type="button" onClick={() => setState({ phase: 'loading' })}>
            {tError('station_details_error_retry')}
          </button>
        </main>
      </div>
    )
  }

  // An id no station has — an old or mistyped NaPTAN. nginx cannot tell it
  // from a real station that has no prerendered file (DLR, Overground,
  // Elizabeth line, …), so it answers the shell at 200, and an indexable
  // "not found" is exactly what Google files under Soft 404 (GSC 27.10:
  // `/de/station/940GZZLUCPM`). `noindex` takes it out of the index instead.
  if (state.phase === 'not-found') {
    return (
      <div className={styles.page}>
        <Helmet>
          <title>{t('not_found_title')}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <GlobalHeader locale={locale} stationName={t('breadcrumb_not_found')} />
        <main className={styles.notFound}>
          <h1>{t('not_found_title')}</h1>
          <p>
            {t('not_found_no_match', { id: '' })}
            <code>{stationId}</code> <a href={`/${locale}`}>{t('not_found_open_map')}</a>
          </p>
        </main>
      </div>
    )
  }

  const stationLines = [state.station.line, ...state.otherLines]
    .map((line) => ({
      name: line.localName ?? line.id,
      colorHex: lineHexForTheme(line, theme),
    }))
    .sort((a, b) => a.name.localeCompare(b.name, 'en'))

  const faqEntries = buildStationFaq(
    {
      stationName: state.station.localName,
      zone: state.station.zone ?? undefined,
      lineNames: stationLines.map(({ name }) => name),
      stepFree: accessibilityFacts(state.station.facilities ?? []).stepFree,
      hasToilets: facilityAvailability(state.station.facilities ?? []).TOILETS,
      exitLandmark: exitLandmarkFromExits(content.exits ?? []),
      openRange: openTodayRange(state.timetable ?? state.station.stationTimetable, new Date()),
    },
    t,
    locale,
  )

  return (
    <div className={styles.page}>
      <StationPageMeta
        locale={locale}
        station={state.station}
        lineNames={stationLines.map(({ name }) => name)}
        address={content.address}
      />
      <GlobalHeader locale={locale} stationName={state.station.localName} />
      {/* StationStatusController owns the live-status preloader (60s
          poll) for the Overview's Service fact — same provider AppShell
          mounts for the map surfaces. */}
      <StationStatusController>
        <main>
          {/* Design order (Figma 8597:41520): hero first, the anchor tab
              row under it, then the content sections — Overview's content
              block ("Other lines", 8597:41539) opens them. */}
          <section aria-label={t('hero_aria')} className={styles.section}>
            <StationOverview
              station={state.station}
              locale={locale}
              content={content}
              timetable={state.timetable}
            />
          </section>
          <StationNav />
          <section id="overview" aria-label={t('nav_overview')} className={styles.contentSection}>
            <StationLinesSection
              stationName={state.station.localName}
              naptanId={state.station.naptanId ?? state.station.id}
              locale={locale}
              lines={state.otherLines}
            />
          </section>
          {CITY.arrivals && (
            <section
              id="live-arrivals"
              aria-label={t('nav_live_arrivals')}
              className={styles.contentSection}
            >
              <StationArrivalsSection
                naptanId={state.station.naptanId ?? state.station.id}
                tflLineId={state.station.line.slug ?? state.station.line.id}
                lineDisplayName={state.station.line.localName ?? state.station.line.id}
                lineColor={lineHexForTheme(state.station.line, theme)}
                stationName={state.station.localName}
              />
            </section>
          )}
          <section id="timetable" aria-label={t('nav_timetable')} className={styles.contentSection}>
            <StationTimetableSection
              stationName={state.station.localName}
              lineDisplayName={state.station.line.localName ?? state.station.line.id}
              timetable={state.station.stationTimetable}
            />
          </section>
          <section aria-label={t('exits_caption')} className={styles.contentSection}>
            <StationExitsSection
              stationName={state.station.localName}
              coordinate={state.station.coordinate}
              exits={content.exits ?? []}
            />
          </section>
          <section
            id="facilities"
            aria-label={t('facilities_aria')}
            className={styles.contentSection}
          >
            <StationFacilitiesSection facilities={state.station.facilities ?? []} />
          </section>
          <section
            id="about"
            aria-label={t('about_heading', { station: state.station.localName })}
            className={styles.contentSection}
          >
            <StationAboutSection
              stationName={state.station.localName}
              zone={state.station.zone ?? undefined}
              street={streetFromAddress(content.address)}
              lines={stationLines}
              landmarks={stationLandmarks(content, state.station.localName)}
            />
          </section>
          <section id="faq" aria-label={t('faq_aria')} className={styles.contentSection}>
            <StationFaqSection entries={faqEntries} />
          </section>
          {/* Full-bleed conversion banner — outside the gutter container
              (its gradient background spans the viewport). */}
          <section aria-label={t('promo_aria')}>{CITY.storeApps && <StationAppPromo />}</section>
          <SiteFooter />
        </main>
      </StationStatusController>
    </div>
  )
}
